import { cn } from '@invent/wl-ui-kit-next';
import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { PageHeader } from '@/design';
import type { IContentEventBody } from '../api/event-contracts';
import type { IContentPostBody } from '../api/post-contracts';
import { CONTENT_PATHS } from '../paths';
import { draftSlug } from '../shared';

/**
 * The chrome every website-content screen is drawn in (§7.11).
 *
 * It is this domain's own rather than the admin section's `AdminPage`, for one reason that
 * matters and one that follows from it. The reason that matters: **a screen here whose data is
 * invented carries a status strip saying so**, and that strip belongs between the header and the
 * body on every such screen — a page component with a slot for it is how it cannot be forgotten
 * on the eighth one. The reason that follows: `AdminPage` is not exported from the admin
 * domain's barrel, and reaching past a barrel to borrow a component is how two domains stop
 * being separable.
 *
 * The slot is optional because the news screens now read survey-api and have nothing to declare.
 * Optional rather than removed: four screens still do, and the day one of them gets its endpoints
 * the strip has to come off that screen deliberately.
 *
 * The tabs are routes, not state, for the reason the admin section's are: each pane is a URL, so
 * a screen can be linked to, reloaded and driven by a test.
 */

export const WEBSITE_TABS = [
  { to: CONTENT_PATHS.overview, label: 'Overview', end: true },
  { to: CONTENT_PATHS.news, label: 'News & Insights' },
  { to: CONTENT_PATHS.team, label: 'Team' },
  { to: CONTENT_PATHS.events, label: 'Events' },
  { to: CONTENT_PATHS.media, label: 'Media' },
  { to: CONTENT_PATHS.pages, label: 'Pages' },
  { to: CONTENT_PATHS.access, label: 'Who can publish' }
];

export function ContentPage({
  title,
  crumb,
  lede,
  actions,
  strip,
  tabs = true,
  children
}: {
  title: string;
  crumb?: string;
  lede?: React.ReactNode;
  actions?: React.ReactNode;
  /** The invented-data strip — passed by the screens that still draw fixtures, and by no
   *  other. */
  strip?: React.ReactNode;
  /** A record opened from a list draws no tabs — it is not one of the seven places. */
  tabs?: boolean;
  children: React.ReactNode;
}) {
  return (
    <>
      <PageHeader
        title={title}
        crumb={crumb}
        lede={lede}
        actions={actions}
        tabs={tabs ? <WebsiteTabs /> : undefined}
      />
      {strip}
      <div className="grid gap-6 px-6 py-6">{children}</div>
    </>
  );
}

function WebsiteTabs() {
  const { state } = useLocation();
  return (
    <nav aria-label="Section" className="-mx-1 flex flex-wrap gap-x-4">
      {WEBSITE_TABS.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          state={state}
          className={({ isActive }) =>
            cn(
              '-mb-px border-b-2 px-1 pt-1 pb-2 text-[0.8125rem] transition-colors',
              isActive
                ? 'border-[var(--color-teal)] font-medium text-[var(--color-royal-blue)]'
                : 'border-transparent text-muted-foreground hover:text-[var(--color-royal-blue)]'
            )
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * The body "Write a post" sends — as empty as the form allows and no emptier.
 *
 * It carries a placeholder title and today's date, because those are the two fields every new
 * post would otherwise be opened only to fill in, and survey-api refuses a blank title. The slug
 * is a placeholder that cannot collide (`draftSlug`); the editor replaces it with one derived from
 * the title, and once published it stops moving (WEB-06 AC2).
 *
 * No author either. It is the by-line the site prints, not the account that wrote it, and
 * defaulting it to whoever pressed the button is how a research team's piece goes out under an
 * administrator's name.
 */
/**
 * The event "New event" stands up: a title the author replaces and a pair of dates they adjust.
 *
 * Dates rather than nothing, because the server requires a range and a control with no value is
 * a date an author has to find before they can type anything else. Two hours from now: long
 * enough to be a plausible event, short enough that nobody mistakes it for a real one.
 */
export function NEW_EVENT(): IContentEventBody {
  const now = new Date();
  return {
    name: 'Untitled event',
    startsAt: now.toISOString(),
    endsAt: new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString(),
    venue: '',
    city: '',
    state: '',
    description: '',
    registrationUrl: '',
    template: 'national',
    sessions: [],
    speakers: [],
    slug: draftSlug('Untitled event'),
    seo: { title: '', description: '', imageId: null }
  };
}

export function NEW_POST(): IContentPostBody {
  return {
    kind: 'news',
    title: 'Untitled post',
    slug: draftSlug('Untitled post'),
    summary: '',
    body: '',
    author: '',
    publishedOn: new Date().toISOString().slice(0, 10),
    seo: { title: '', description: '', imageId: null },
    leadImageId: null
  };
}
